import React from 'react';
import { styled, Typography } from '@mui/material';
import { Code } from 'common/components/display';
import { Trans, useTranslation } from 'services/localization';

const ImportInstructions = () => {
	const { t } = useTranslation();
	return (
		<>
			<Typography variant="h6" sx={{ mb: 2 }}>
				{t('inventory.import.howWorks.title', 'How does it work?')}
			</Typography>
			<Typography variant="body2" sx={{ fontWeight: 400 }} gutterBottom>
				{t(
					'inventory.import.howWorks.section1',
					'Add new Articles, SKUs and Specifications, or update existing ones by uploading a file.',
				)}
			</Typography>
			<Typography variant="body2" sx={{ fontWeight: 400 }} gutterBottom>
				{t(
					'inventory.import.howWorks.section2',
					'You can export your existing inventory to get a CSV file in the correct format. Edit existing rows or add new rows and columns to the CSV file, and import it back. You can still review the additions before confirming the import.',
				)}
			</Typography>
			<Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
				{t('inventory.import.goodToKnow.title', 'Good to know')}
			</Typography>
			<List>
				<ListItem>
					<Trans
						i18nKey="inventory.import.goodToKnow.section1"
						defaults="The first column of the CSV file must always be <code>{{id}}</code> or <code>{{article_code}}</code>. Do not edit the values of the first row or the first column of the exported CSV file."
						components={{
							code: <Code />,
						}}
						values={{
							id: 'id',
							article_code: 'article_code',
						}}
					/>
				</ListItem>
				<ListItem>
					<Trans
						i18nKey="inventory.import.goodToKnow.section2"
						defaults="If an Article has empty <code>{{id}}</code> column, and no article with the given <code>{{article_code}}</code> value is found in inventory, a new Article will be created. If an existing Article is found, it will be updated with the content from the imported file."
						components={{
							code: <Code />,
						}}
						values={{
							id: 'id',
							article_code: 'article_code',
						}}
					/>
				</ListItem>
				<ListItem>
					{t(
						'inventory.import.goodToKnow.section3',
						'Articles cannot be removed using a CSV import.',
					)}
				</ListItem>
				<ListItem>
					{t(
						'inventory.import.goodToKnow.section4',
						'New columns that do not exist in your inventory will be added as new Specifications.',
					)}
				</ListItem>
				<ListItem>
					{t(
						'inventory.import.goodToKnow.section5',
						'New SKU codes that do not exist in your inventory will be added as new SKUs.',
					)}
				</ListItem>
				<ListItem>
					{t(
						'inventory.import.goodToKnow.section6',
						'The SKU code of an existing SKU cannot be edited using CSV import.',
					)}
				</ListItem>
				<ListItem>
					{t(
						'inventory.import.goodToKnow.section7',
						'If a column is not included in the imported file, but is included in the inventory, then the value in the inventory remains the same.',
					)}
				</ListItem>
				<ListItem>
					{t(
						'inventory.import.goodToKnow.section8',
						'If a value of a column in the imported file is blank, the matching value in the inventory is overwritten as blank.',
					)}
				</ListItem>
			</List>
		</>
	);
};

const List = styled('ul')(({ theme }) => ({
	paddingLeft: '20px',
	margin: 0,
}));

const ListItem = styled('li')(({ theme }) => ({
	margin: theme.spacing(1.5, 0),
	fontFamily: theme.typography.fontFamily,
	fontSize: 14,
	fontWeight: 400,
}));

export default ImportInstructions;
